import React, { useEffect, useState } from 'react';
import { getScheduledPayments, deleteScheduledPayment, retryScheduledPayment, updateScheduledPayment } from '../api/scheduledPayments';
import { ScheduledPayment } from '../types/models';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, IconButton, CircularProgress, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Select, MenuItem, Tooltip, TextField
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import ScheduledPaymentForm from '../components/ScheduledPaymentForm';
import { API_BASE } from '../api/config';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Address from '../components/Address';
import { formatDateTime, formatAmount, formatAmountParts } from '../utils/format';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { TWAP_DEFAULT_MIN, TWAP_DEFAULT_MAX, TWAP_DEFAULT_INTERVAL } from '../constants/twap';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';


const statusColors: Record<string, string> = {
  scheduled: '#1976d2',
  pending: '#ff9800',
  executed: '#388e3c',
  failed: '#d32f2f',
  cancelled: '#757575',
};

export default function ScheduledPaymentsPage() {
  const [payments, setPayments] = useState<ScheduledPayment[]>([]);
  // Canonical month state as 'YYYY-MM' string
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<ScheduledPayment | null>(null);
  const [retryingPayment, setRetryingPayment] = useState<ScheduledPayment | null>(null);
  const [confirmRetryOpen, setConfirmRetryOpen] = useState(false);
  
  const [initialFormValues, setInitialFormValues] = useState<Partial<ScheduledPayment> | null>(null);
  const [pendingStatus, setPendingStatus] = useState<ScheduledPayment['status'] | null>(null);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const [statusChangePayment, setStatusChangePayment] = useState<ScheduledPayment | null>(null);

  const [amountDialogOpen, setAmountDialogOpen] = useState(false);
  const [amountEditPayment, setAmountEditPayment] = useState<ScheduledPayment | null>(null);
  const [newAmount, setNewAmount] = useState<string>('');

  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [timeEditPayment, setTimeEditPayment] = useState<ScheduledPayment | null>(null);
  const [newTime, setNewTime] = useState<string>('');

  const [twapMin, setTwapMin] = useState<string>('');
  const [twapMax, setTwapMax] = useState<string>('');
  const [twapInterval, setTwapInterval] = useState<string>('');
  const [useTwapEdit, setUseTwapEdit] = useState<boolean>(false);

  useEffect(() => {
    if (useTwapEdit && !twapInterval) setTwapInterval('3600');
  }, [useTwapEdit]);

  const fetchPayments = async (monthStr = currentMonth) => {
    setLoading(true);
    setError(null);
    try {
      const tzOffset = -new Date().getTimezoneOffset();
      const data = await getScheduledPayments({ month: `${monthStr}-01`, tz_offset: tzOffset });
      setPayments(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load scheduled payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(currentMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const goToPrevMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 2, 1); // m-2: previous month (Date months are 0-based)
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const goToNextMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m, 1); // m: next month
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const handleMonthPick = (e: React.ChangeEvent<HTMLInputElement>) => setCurrentMonth(e.target.value);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState<ScheduledPayment | null>(null);
  const handleAskDelete = (payment: ScheduledPayment) => {
    setDeletingPayment(payment);
    setConfirmDeleteOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!deletingPayment) return;
    try {
      await deleteScheduledPayment(deletingPayment.id);
      await fetchPayments();
    } catch (e: any) {
      alert(e.message || 'Failed to delete scheduled payment');
    }
    setConfirmDeleteOpen(false);
    setDeletingPayment(null);
    handleCloseAmountDialog();
  };
  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setDeletingPayment(null);
    handleCloseAmountDialog();
  };

  const handleOpen = () => { setInitialFormValues(null); setOpen(true); };
  const handleClose = () => setOpen(false);

  // Removed unused handleEdit; edit flow will be implemented later via ScheduledPaymentForm

  const handleRetry = (payment: ScheduledPayment) => {
    setRetryingPayment(payment);
    setConfirmRetryOpen(true);
  };
  const handleConfirmRetry = async () => {
    if (!retryingPayment) return;
    try {
      await retryScheduledPayment(retryingPayment.id);
      await fetchPayments();
      // Optionally show a success message
    } catch (e: any) {
      alert(e.message || 'Failed to retry scheduled payment');
    }
    setConfirmRetryOpen(false);
    setRetryingPayment(null);
    handleCloseAmountDialog();
  };
  const handleCancelRetry = () => {
    setConfirmRetryOpen(false);
    setRetryingPayment(null);
    handleCloseAmountDialog();
  };

  const handleExportCSV = () => {
    const monthParam = `${currentMonth}-01`;
    const url = `${API_BASE}/scheduled_payments.csv?month=${encodeURIComponent(monthParam)}`;
    window.open(url, '_blank');
  };

  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_BASE}/scheduled_payments/import`, {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('piCustodianAuthToken') || ''}` },
      });
      if (!response.ok) {
        let errorMsg = 'Import failed';
        try {
          const data = await response.json();
          errorMsg = data.error || data.message || errorMsg;
        } catch {
          const text = await response.text();
          errorMsg = text || errorMsg;
        }
        throw new Error(errorMsg);
      }
      alert('Import successful!');
      fetchPayments();
    } catch (e: any) {
      alert('Import failed: ' + (e.message || e));
    }
    event.target.value = '';
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const triggerFileInput = () => fileInputRef.current?.click();

  const handleStatusChange = async (payment: ScheduledPayment, newStatus: ScheduledPayment['status']) => {
    if (payment.status === newStatus) return;
    try {
      if (newStatus === 'scheduled') {
        await retryScheduledPayment(payment.id);
      } else {
        await updateScheduledPayment(payment.id, { status: newStatus });
      }
      await fetchPayments();
    } catch (e: any) {
      alert(e.message || 'Failed to update status');
    }
  };

  // inline status editor removed; status is edited inside the modal

  const handleConfirmStatus = async () => {
    if (!statusChangePayment || !pendingStatus) {
      setConfirmStatusOpen(false);
      handleCloseAmountDialog();
      return;
    }
    await handleStatusChange(statusChangePayment, pendingStatus);
    setConfirmStatusOpen(false);
    setPendingStatus(null);
    setStatusChangePayment(null);
    handleCloseAmountDialog();
  };

  const handleCancelConfirmStatus = () => {
    setConfirmStatusOpen(false);
    setPendingStatus(null);
    setStatusChangePayment(null);
    handleCloseAmountDialog();
  };

  // amount-only opener removed; pencil opens combined modal
  const handleCloseAmountDialog = () => {
    setAmountDialogOpen(false);
    setTimeDialogOpen(false);
    setAmountEditPayment(null);
    setTimeEditPayment(null);
    setNewAmount('');
    setNewTime('');
    setTwapInterval('');
    setTwapMin('');
    setTwapMax('');
  };
  const handleSaveAmount = async () => {
    const payment = amountEditPayment || timeEditPayment;
    if (!payment) return;
    const amountTrimmed = (newAmount || '').toString().trim();
    const timeTrimmed = (newTime || '').toString().trim();
    const payload: any = {};
    if (amountTrimmed) {
      if (isNaN(Number(amountTrimmed)) || Number(amountTrimmed) <= 0) {
        alert('Please enter a valid amount > 0');
        return;
      }
      if (amountTrimmed !== payment.amount) payload.amount = amountTrimmed;
    }
    if (timeTrimmed) {
      const iso = toISOFromLocalInput(timeTrimmed);
      if (iso && iso !== payment.scheduled_for) payload.scheduled_for = iso as any;
    }
    payload.use_twap = useTwapEdit as any;
    if (useTwapEdit) {
      payload.twap_payment_attributes = {
        slice_min_amount: twapMin || TWAP_DEFAULT_MIN,
        slice_max_amount: twapMax || TWAP_DEFAULT_MAX,
        slice_interval_seconds: twapInterval || TWAP_DEFAULT_INTERVAL,
      };
    }
    if (Object.keys(payload).length === 0) {
      handleCloseAmountDialog();
      return;
    }
    try {
      await updateScheduledPayment(payment.id, payload);
      await fetchPayments();
      handleCloseAmountDialog();
    } catch (e: any) {
      alert(e.message || 'Failed to save changes');
    }
  };

  const toLocalDateTimeInputValue = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  };

  const toISOFromLocalInput = (localValue: string) => {
    if (!localValue) return '';
    const d = new Date(localValue);
    return d.toISOString();
  };

  type ScheduledPaymentWithTwap = ScheduledPayment & { twap_payment?: { slice_interval_seconds?: number } };
  const handleOpenTimeDialog = (payment: ScheduledPaymentWithTwap) => {
    setTimeEditPayment(payment);
    setNewAmount(payment.amount);
    setNewTime(toLocalDateTimeInputValue(payment.scheduled_for));
    setPendingStatus(payment.status);
    setStatusChangePayment(payment);
    setTwapMin(payment.twap_payment?.slice_min_amount || '');
    setTwapMax(payment.twap_payment?.slice_max_amount || '');
    setTwapInterval(payment.twap_payment?.slice_interval_seconds?.toString() || '');
    setUseTwapEdit(!!payment.use_twap);
    setTimeDialogOpen(true);
  };

  const handleCloseTimeDialog = () => {
    setTimeDialogOpen(false);
    setAmountDialogOpen(false);
    setTimeEditPayment(null);
    setAmountEditPayment(null);
    setNewTime('');
    setNewAmount('');
    setTwapInterval('');
    setTwapMin('');
    setTwapMax('');
  };
  // handleSaveTime no longer needed separately; using handleSaveAmount to save both fields

  return (
    <Box>
      <Box mb={1}>
        <Alert severity="info">
          <strong>How to execute due scheduled payments:</strong><br />
          This page lists all scheduled payments. Payments that are due are highlighted.<br />
          To execute all due payments, run the following command on the backend:<br /><br />
          <code>PI_NETWORK=mainnet bin/rails scheduled_payments:execute_due</code><br /><br />
          This will submit all due payments to the blockchain and update their status.
          <br /><br />
          To execute due payments, and twap payments in a loop, run the following command on the backend:<br /><br />
          <code>INTERVAL=3650 PI_NETWORK=mainnet bin/rails scheduled_payments:loop_execute_due</code><br /><br />
          This will execute due payments in a loop with a 3650 second interval. 
        </Alert>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h4">Scheduled Payments</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Previous month">
            <IconButton aria-label="Previous month" onClick={goToPrevMonth} size="small">
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            type="month"
            size="small"
            variant="outlined"
            value={currentMonth}
            onChange={handleMonthPick}
            inputProps={{
              'aria-label': 'Select month',
              onClick: (e: any) => e.currentTarget?.showPicker?.(),
            }}
            sx={{
              minWidth: 200,
              '& .MuiInputBase-root': { height: 36, padding: 0 },
              '& input': { padding: '6px 10px', height: 36, boxSizing: 'border-box' },
            }}
          />
          <Tooltip title="Next month">
            <IconButton aria-label="Next month" onClick={goToNextMonth} size="small">
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ width: 16 }} />
          <Button variant="outlined" color="primary" onClick={handleExportCSV} sx={{ mr: 1 }}>
            Export CSV
          </Button>
          <Button variant="outlined" color="primary" onClick={triggerFileInput} sx={{ mr: 1 }}>
            Import CSV
          </Button>
          <input
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleImportCSV}
          />
          <Button variant="contained" color="primary" onClick={handleOpen}>
            + Schedule Payment
          </Button>
        </Box>
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPayment ? 'Reschedule Payment' : 'Schedule Payment'}</DialogTitle>
        <DialogContent>
          <ScheduledPaymentForm payment={editingPayment} initial={initialFormValues as any} onSuccess={() => { setOpen(false); setEditingPayment(null); setInitialFormValues(null); fetchPayments(); }} onCancel={handleClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={timeDialogOpen} onClose={handleCloseTimeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Amount, Time & Status</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Amount"
            type="number"
            value={newAmount}
            onChange={(e) => setNewAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.0000001' }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Scheduled For"
            type="datetime-local"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {(
            <>
              {true && (
                <Box sx={{ mt: 2 }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={useTwapEdit}
                      onChange={(e) => setUseTwapEdit(e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    Deliver as time-sliced payment (TWAP)
                  </label>
                </Box>
              )}
              {useTwapEdit && (
                <React.Fragment>
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Min Slice Amount"
                    type="number"
                    value={twapMin}
                    onChange={e => setTwapMin(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Max Slice Amount"
                    type="number"
                    value={twapMax}
                    onChange={e => setTwapMax(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Slice Interval (seconds)"
                    type="number"
                    value={twapInterval}
                    onChange={e => setTwapInterval(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                </React.Fragment>
              )}
            </>
          )}
          <Select
            size="small"
            value={pendingStatus || (statusChangePayment?.status || 'scheduled')}
            onChange={(e) => {
              setPendingStatus(e.target.value as ScheduledPayment['status']);
            }}
            sx={{ mt: 2, minWidth: 160 }}
          >
            <MenuItem value="scheduled">scheduled</MenuItem>
            <MenuItem value="pending">pending</MenuItem>
            <MenuItem value="executed">executed</MenuItem>
            <MenuItem value="failed">failed</MenuItem>
            <MenuItem value="cancelled">cancelled</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions sx={{ px: 3 }}>
          {(amountEditPayment || timeEditPayment) && (
            <Button color="error" onClick={() => {
              const p = amountEditPayment || timeEditPayment;
              if (p) {
                setDeletingPayment(p);
                setConfirmDeleteOpen(true);
              }
            }}>Delete</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseTimeDialog}>Cancel</Button>
          <Button onClick={async () => {
            // First, if status changed, confirm and apply via existing flow
            if (statusChangePayment && pendingStatus && pendingStatus !== statusChangePayment.status) {
              setConfirmStatusOpen(true);
              // do not close; let confirmation flow complete, amount/time save after confirm
              return;
            }
            await handleSaveAmount();
          }} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmStatusOpen} onClose={handleCancelConfirmStatus}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          {statusChangePayment && pendingStatus ? (
            <>
              Change status for payment #{statusChangePayment.id} from <strong>{statusChangePayment.status}</strong> to <strong>{pendingStatus}</strong>?
              {pendingStatus === 'scheduled' && (
                <>
                  <br />
                  <span style={{ color: '#d32f2f' }}>
                    This will also clear <code>executed_at</code> and <code>transaction_hash</code> for this payment.
                  </span>
                </>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmStatus}>Cancel</Button>
          <Button onClick={handleConfirmStatus} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmRetryOpen} onClose={handleCancelRetry}>
        <DialogTitle>Retry Payment</DialogTitle>
        <DialogContent>
          Are you sure you want to retry this payment? This will reset status to scheduled.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRetry}>Cancel</Button>
          <Button onClick={handleConfirmRetry} color="primary" variant="contained">Retry</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Scheduled Payment</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this scheduled payment?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={amountDialogOpen} onClose={handleCloseAmountDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Amount, Time & Status</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Amount"
            type="number"
            value={newAmount}
            onChange={(e) => setNewAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.0000001' }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Scheduled For"
            type="datetime-local"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Select
            size="small"
            value={pendingStatus || (statusChangePayment?.status || 'scheduled')}
            onChange={(e) => {
              setPendingStatus(e.target.value as ScheduledPayment['status']);
            }}
            sx={{ mt: 2, minWidth: 160 }}
          >
            <MenuItem value="scheduled">scheduled</MenuItem>
            <MenuItem value="pending">pending</MenuItem>
            <MenuItem value="executed">executed</MenuItem>
            <MenuItem value="failed">failed</MenuItem>
            <MenuItem value="cancelled">cancelled</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions sx={{ px: 3 }}>
          {(amountEditPayment || timeEditPayment) && (
            <Button color="error" onClick={() => {
              const p = amountEditPayment || timeEditPayment;
              if (p) {
                setDeletingPayment(p);
                setConfirmDeleteOpen(true);
              }
            }}>Delete</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseAmountDialog}>Cancel</Button>
          <Button onClick={async () => {
            if (statusChangePayment && pendingStatus && pendingStatus !== statusChangePayment.status) {
              setConfirmStatusOpen(true);
              return;
            }
            await handleSaveAmount();
          }} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : payments.length === 0 ? (
        <Typography>No scheduled payments found.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ tableLayout: 'auto', minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ maxWidth: 56 }}>ID</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>Source Account</TableCell>
                <TableCell sx={{ maxWidth: 200 }}>Source Owner</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>Target Account</TableCell>
                <TableCell sx={{ maxWidth: 200 }}>Target Owner</TableCell>
                <TableCell sx={{ maxWidth: 120 }}>Amount</TableCell>
                <TableCell sx={{ maxWidth: 220 }}>Scheduled For</TableCell>
                <TableCell sx={{ maxWidth: 140 }}>Status</TableCell>
                <TableCell sx={{ maxWidth: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map(payment => (
                <TableRow key={payment.id} style={(payment.due && payment.status !== 'executed') ? { backgroundColor: '#fff3e0' } : {}}>
                  <TableCell sx={{ maxWidth: 56 }}>{payment.id}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Address address={payment.source_account_id} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography component="span" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {payment.source_owner_name || ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Address address={payment.target_account_id} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography component="span" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {payment.target_owner_name || ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 120, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }}>
                    {(() => {
                      const [main, hasEllipsis] = formatAmountParts(payment.amount);
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'baseline', justifyContent: 'flex-end' }}>
                          <span style={{ minWidth: 98, textAlign: 'right', display: 'inline-block' }}>{main}</span>
                          {hasEllipsis && (
                            <span style={{ display: 'inline-block', minWidth: 8, marginLeft: '-2px', color: '#888', fontSize: '1.15em', fontWeight: 400 }}>…</span>
                          )}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {/* For TWAPs, show date, clock, and, if DUE, ETA below */}
                    {payment.use_twap ? (
                      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          {formatDateTime(payment.scheduled_for)}
                          <AccessTimeOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', color: '#1976d2', ml: 0.8 }} />
                        </span>
                        {payment.due && payment.twap_payment?.eta_estimate_to_complete_string && (
                          <span style={{ color: '#888', fontSize: '0.92em', fontWeight: 400, marginLeft: 1, marginTop: 2 }}>
                            ETA: in {payment.twap_payment.eta_estimate_to_complete_string}
                          </span>
                        )}
                      </span>
                    ) : (
                      formatDateTime(payment.scheduled_for)
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 140 }}>
                    {payment.status === 'executed' ? (
                      <span style={{ color: '#388e3c', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircleRoundedIcon sx={{ color: '#37b24d', fontSize: 18, mr: 0.5 }} />
                        Done
                      </span>
                    ) : payment.status === 'failed' ? (
                      <span style={{
                        color: '#d32f2f',
                        fontWeight: 600,
                        border: '1.5px solid #d32f2f',
                        background: '#fde8ea',
                        borderRadius: 10,
                        padding: '2px 12px',
                        display: 'inline-block',
                        fontSize: '0.98em',
                        letterSpacing: '0.01em',
                      }}>Failed</span>
                    ) : payment.status === 'cancelled' ? (
                      <span style={{ color: '#757575', fontWeight: 500 }}>Cancelled</span>
                    ) : payment.status === 'scheduled' && payment.due ? (
                      <span style={{ color: '#d84315', fontWeight: 600, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <ReportProblemRoundedIcon sx={{ color: '#d84315', fontSize: 18, mr: 0.5 }} />
                          DUE
                        </span>
                        {payment.use_twap && payment.twap_payment && payment.twap_payment.amount_total && Number(payment.twap_payment.amount_total) > 0 ? (() => {
                          const paid = Number(payment.twap_payment.amount_paid_so_far || 0);
                          const total = Number(payment.twap_payment.amount_total);
                          const percent = Math.round((paid / total) * 100);
                          return percent < 100 ? (
                            <span style={{ color: '#888', fontSize: '0.92em', fontWeight: 400, marginLeft: 2 }}>{percent}% completed</span>
                          ) : null;
                        })() : null}
                      </span>
                    ) : payment.status === 'scheduled' && !payment.due ? (
                      <span style={{ color: '#1976d2', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <EventAvailableIcon sx={{ color: '#1976d2', fontSize: 18, mr: 0.5 }} />
                        Scheduled
                      </span>
                    ) : (
                      <span>{payment.status}</span>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 120, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                      <Tooltip title="Duplicate (new payment with same accounts)">
                        <IconButton
                          aria-label="duplicate"
                          size="small"
                          onClick={() => {
                            setEditingPayment(null);
                            setInitialFormValues({
                              source_account_id: payment.source_account_id,
                              target_account_id: payment.target_account_id,
                              amount: payment.amount,
                              scheduled_for: new Date().toISOString(),
                              use_twap: payment.use_twap,
                              twap_payment: payment.use_twap && payment.twap_payment ? {
                                slice_min_amount: payment.twap_payment.slice_min_amount || TWAP_DEFAULT_MIN,
                                slice_max_amount: payment.twap_payment.slice_max_amount || TWAP_DEFAULT_MAX,
                                slice_interval_seconds: payment.twap_payment.slice_interval_seconds?.toString() || TWAP_DEFAULT_INTERVAL,
                              } : undefined,
                            } as any);
                            setOpen(true);
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit amount, time & status">
                        <IconButton aria-label="edit-amount-time" onClick={() => handleOpenTimeDialog(payment)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {payment.status === 'failed' && (
                        <Tooltip title="Retry (reset to scheduled)">
                          <IconButton aria-label="retry" onClick={() => handleRetry(payment)} size="small">
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Removed separate amount edit; pencil now edits both */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
} 